import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getSiteAssetStatus,
  useSiteAssetMap,
} from '~/app/hooks/site/useSiteAssetMap';
import type { SiteAssetMapAsset } from '~/app/services/site/site.service';
import { formatDateTime } from '~/app/utils/format-date-time';
import { SiteAssetMapPageView } from './SiteAssetMapPageView';

export function SiteAssetMapPage() {
  const params = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('sites');
  const [mapMode, setMapMode] = useState<'focused' | 'standard'>('focused');
  const siteId = typeof params.siteId === 'string' ? params.siteId : '';
  const { assets, error, isFetching, isLoading, refetch, site, statusCounts } =
    useSiteAssetMap(siteId);

  const columns: ColumnsType<SiteAssetMapAsset> = [
    {
      title: t('detail.table.assetName', 'Asset name'),
      dataIndex: 'assetName',
      key: 'assetName',
      render: (_value, asset) => (
        <Link to={`/assets/${asset.id}`}>{asset.assetName}</Link>
      ),
    },
    {
      title: t('detail.table.assetType', 'Asset type'),
      dataIndex: 'assetTypeName',
      key: 'assetTypeName',
    },
    {
      title: t('detail.table.assetModel', 'Asset model'),
      dataIndex: 'assetModelName',
      key: 'assetModelName',
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('detail.table.zone', 'Zone'),
      dataIndex: 'zoneName',
      key: 'zoneName',
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('detail.table.openIncidents', 'Open incidents'),
      dataIndex: 'openIncidentsCount',
      key: 'openIncidentsCount',
    },
    {
      title: t('detail.table.status', 'Status'),
      key: 'status',
      render: (_value, asset) => {
        const status = getSiteAssetStatus(asset.openIncidentsCount);

        return t(`detail.status.${status}`, status);
      },
    },
    {
      title: t('detail.table.installedAt', 'Installed at'),
      dataIndex: 'installedAt',
      key: 'installedAt',
      render: (value: string | null) => formatDateTime(value),
    },
  ];

  return (
    <SiteAssetMapPageView
      assets={assets}
      columns={columns}
      error={error}
      isFetching={isFetching}
      isLoading={isLoading}
      mapMode={mapMode}
      onBack={() => navigate('/sites')}
      onMapModeChange={setMapMode}
      onRefresh={() => {
        void refetch();
      }}
      site={site}
      statusCounts={statusCounts}
    />
  );
}
